import { Controller } from "@hotwired/stimulus"

const DEBOUNCE_TIME_MS = 250
export default class extends Controller {
  static targets = [
    "input",
    "results",
    "resultApiTemplate",
    "resultNodeTemplate",
    "searchTutorial",
    "keyboardNavigationTutorial",
    "caretTemplate",
    "form",
    "loader",
    "searchIcon",
    "moreResults",
    "noResultsTemplate",
    "resultsListTemplate",
    "dialogFooter"
  ]

  static values = {
    limit: Number,
    offset: Number,
    searchUrl: String,
    searchContext: String
  }

  initialize() {
    this.placeholderAlreadySet = false
  }

  async connect() {
    this.tabindex = -1
    await this.#setPlaceholder()
  }

  async search(query, isNewSearch = true) {
    if (isNewSearch) {
      this.#resetOffset()
    }
    const results = await this.#performBackendSearch(query)
    this.#processSearchResults(results)
    this.#incrementOffset()
  }

  submitOnType() {
    clearTimeout(this.timeout)
    if (this.inputTarget.value.length > 0) {
      this.#toggleLoader(true)
      this.timeout = setTimeout(() => {
        this.search(this.inputTarget.value)
      }, DEBOUNCE_TIME_MS)
    } else {
      this.#resetDialog()
      this.#showSearchTutorial()
      this.#showDialogFooter()
    }
  }

  async onFocus() {
    this.#resetDialog()
    this.inputTarget.value = ""
    this.inputTarget.focus()
    this.#showSearchTutorial()
    this.#hideKeyboardNavigationTutorial()
    await this.#setPlaceholder()
  }

  #resetDialog() {
    this.#clearResults()
    this.#resetOffset()
    this.#showDialogFooter()
    this.#hideShowMoreButton()
    this.#hideKeyboardNavigationTutorial()
  }

  previousElement(event) {
    event.preventDefault()
    const results = this.resultsTarget.querySelectorAll(".result-link")
    if (this.tabindex - 1 < 0) {
      this.inputTarget.focus()
      this.tabindex = -1
    } else {
      const focus = results[this.tabindex -= 1]
      this.#setFocus(focus)
    }
  }

  nextElement(event) {
    event.preventDefault()
    const results = this.resultsTarget.querySelectorAll(".result-link")
    if (this.tabindex + 1 < results.length) {
      const focus = results[this.tabindex += 1]
      this.#setFocus(focus)
    }
  }

  #hideShowMoreButton(event) {
    // this condition hides the more results button when
    // - the user clicks on ESC in the UI
    // - the user clicks on Dialog backdrop
    // - the user types ESC in the input field
    if (!event || event.target === event.currentTarget || (event.type === "keydown" && event.code === "Escape")) {
      this.moreResultsTarget.classList.add("hidden")
    }
  }

  #showShowMoreButton() {
    this.moreResultsTarget.classList.remove("hidden")
  }

  #hideKeyboardNavigationTutorial() {
    this.keyboardNavigationTutorialTarget.classList.add("hidden")
  }

  #showKeyboardNavigationTutorial() {
    this.keyboardNavigationTutorialTarget.classList.remove("hidden")
  }

  #hideSearchTutorial() {
    this.searchTutorialTarget.classList.add("hidden")
  }

  #showSearchTutorial() {
    this.searchTutorialTarget.classList.remove("hidden")
  }

  #hideDialogFooter() {
    this.dialogFooterTarget.classList.add("hidden")
  }

  #showDialogFooter() {
    this.dialogFooterTarget.classList.remove("hidden")
  }

  #setFocus(element) {
    element.focus()
    element.scrollIntoView({ block: "center", behavior: "instant" })
  }

  loadMoreResults(e) {
    e.preventDefault()
    this.search(this.inputTarget.value, false) // false = not a new search
  }

  async #performBackendSearch(query, options = {}) {
    const params = new URLSearchParams({
      query: query,
      offset: this.offsetValue,
      limit: this.limitValue,
      uid: this.#generateUID(),
      ...options
    })
    const targetUrl = new URL(this.searchUrlValue)
    targetUrl.search = params.toString()

    const response = await fetch(targetUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return await response.json()
  }

  #generateUID() {
    // this is used to track the search sessions
    // using crypto API for secure, performant unique id generation
    return Array.from(crypto.getRandomValues(new Uint8Array(8)))
      .map(b => b.toString(16).padStart(2, "0"))
      .join("")
  }

  async #setPlaceholder() {
    if (this.placeholderAlreadySet) return

    let examples = []
    if (this.searchContextValue === "hub") {
      examples = ["APIs", "Endpoints", "Webhooks", "Models"]
    } else {

      // estimate number of Endpoints for the current API
      const estimationResponse = await this.#performBackendSearch(
        "",
        {
          limit: 1,
          filter: "node_type = 'Endpoint'"
        }
      )

      const estimatedTotalHits = estimationResponse.estimatedTotalHits

      if (estimatedTotalHits === 0) return

      const numberOfExamples = 3

      examples = await Promise.all(
        Array.from({ length: numberOfExamples }, async () => {
          let name = ""

          while (name === "") {
            const randomOffset = Math.floor(Math.random() * estimatedTotalHits)
            const response = await this.#performBackendSearch(
              "",
              {
                limit: 1,
                filter: "node_type = 'Endpoint'",
                offset: randomOffset
              }
            )

            name = response.hits[0].name
          }

          return name
        })
      )
    }

    // Set a simple placeholder for now
    this.inputTarget.placeholder = `"${examples.join("\", \"")}"...`
    this.placeholderAlreadySet = true
  }

  #processSearchResults(results) {
    this.#clearResults()

    if (!results.hits || results.hits.length === 0) {
      this.#displayNoResults(this.inputTarget.value)
      return
    }

    this.#hideSearchTutorial()
    this.#showKeyboardNavigationTutorial()
    this.#showDialogFooter()
    this.#toggleMoreResults(results)

    const resultsList = this.#findOrCreateResultsList()
    this.resultsTarget.appendChild(resultsList)

    if (this.searchContextValue === "hub") {
      results.hits.forEach(hit => {
        const resultNode = hit.node_type ?
          this.#hydrateNodeTemplateWithHit(hit) :
          this.#hydrateApiTemplateWithHit(hit)

        resultsList.appendChild(resultNode)
      })
    } else {
      results.hits.forEach(hit => {
        const resultNode = this.#hydrateNodeTemplateWithHit(hit)
        resultsList.appendChild(resultNode)
      })
    }
  }

  #findOrCreateResultsList() {
    // When the resultsList is already present in the DOM, we don't need to create a new one
    const resultsList = this.resultsTarget.querySelector(".doc-search_v2__results-list")
    if (resultsList) {
      return resultsList
    }

    // Otherwise, we create a new one from the template
    return document.importNode(this.resultsListTemplateTarget.content, true).querySelector(".doc-search_v2__results-list")
  }

  #hydrateNodeTemplateWithHit(hit, context = "node") {
    const resultNode = document.importNode(this.resultNodeTemplateTarget.content, true)
    this.#setResultLinkForTarget(resultNode, hit)

    const apiLabel = resultNode.querySelector(".result-api-name")
    if (hit.api_name && context === "hub") {
      apiLabel.innerHTML = hit.api_name
    } else {
      apiLabel.remove()
    }

    if (!this.#emptyArray(hit._formatted.breadcrumbs)) {
      // wrap last element in a span
      const formattedBreadcrumbs = this.#rejectContentType(hit._formatted.breadcrumbs)
      const lastElement = formattedBreadcrumbs.pop()

      let lastElementContent = lastElement
      if (hit.node_type) {
        lastElementContent = `${lastElement} <span class="result-node-type">${this.#formatNodeType(hit.node_type, hit.subtitle)}</span>`
      }

      const lastElementSpan = `<span class="result-breadcrumbs-last">${lastElementContent}</span>`
      formattedBreadcrumbs.push(lastElementSpan)

      const resultBreadcrumbs = formattedBreadcrumbs.join(`<span class="result-breadcrumbs-caret">${this.caretTemplateTarget.innerHTML}</span>`)

      resultNode.querySelector(".result-breadcrumbs").innerHTML = resultBreadcrumbs
    }

    if (hit._formatted.name && this.#emptyArray(hit.breadcrumbs)) {
      resultNode.querySelector(".result-title").innerHTML = hit._formatted.name
    } else {
      resultNode.querySelector(".result-title").remove()
    }

    if (hit._formatted.description) {
      resultNode.querySelector(".result-description").innerHTML = hit._formatted.description
    } else {
      resultNode.querySelector(".result-description").remove()
    }

    const resultLabel = resultNode.querySelector(".result-label")
    if (hit.label) {
      resultLabel.textContent = hit.label.toUpperCase()
      resultLabel.classList.add(hit.label.toLowerCase())
    } else {
      resultLabel.remove()
    }

    const resultPath = resultNode.querySelector(".result-path")
    if (hit._formatted.subtitle && hit.node_type === "Endpoint") {
      resultPath.innerHTML = this.#formatSubtitleAsPath(hit._formatted.subtitle)
    } else {
      resultPath.remove()
    }

    const pathLabel = resultNode.querySelector(".result-path-label")
    if (!hit.label && (!hit._formatted.subtitle || hit.node_type !== "Endpoint") && pathLabel) {
      pathLabel.remove()
    }

    return resultNode
  }

  #hydrateApiTemplateWithHit(hit) {
    const resultNode = document.importNode(this.resultApiTemplateTarget.content, true)
    this.#setResultLinkForTarget(resultNode, hit)

    if (hit._formatted.name) {
      resultNode.querySelector(".result-title").innerHTML = hit._formatted.name
    }

    if (hit._formatted.description_from_current_definition) {
      resultNode.querySelector(".result-description").innerHTML = hit._formatted.description_from_current_definition
    }

    if (!this.#emptyArray(hit.tags_as_array)) {
      const tags = hit.tags_as_array.map(tag => `<span class="result-label">${tag}</span>`)
      resultNode.querySelector(".result-label").append(...tags)
    } else {
      resultNode.querySelector(".result-label").remove()
    }

    return resultNode
  }

  #setResultLinkForTarget(resultNode, hit) {
    const resultLink = resultNode.querySelector(".result-link")
    resultLink.setAttribute("href", hit.url)
  }

  #clearResults() {
    this.tabindex = -1
    this.#toggleLoader(false)
    this.resultsTarget.replaceChildren()
  }

  #displayNoResults(query) {
    this.#hideShowMoreButton()
    this.#hideKeyboardNavigationTutorial()
    this.#showSearchTutorial()
    this.#hideDialogFooter()
    const noResultsNode = document.importNode(this.noResultsTemplateTarget.content, true)
    noResultsNode.querySelector(".result-query").textContent = query
    this.resultsTarget.replaceChildren(noResultsNode)
  }

  #toggleLoader(toggle) {
    this.loaderTarget.setAttribute("aria-hidden", !toggle)
    this.loaderTarget.setAttribute("aria-busy", toggle)
    this.searchIconTarget.setAttribute("aria-hidden", toggle)
  }

  #emptyArray(array) {
    return array === undefined || Array.isArray(array) && array.length === 0
  }

  #formatNodeType(nodeType, subtitle) {
    let type = nodeType.split("::").pop()

    if (type !== "Endpoint" && type !== "Response" && subtitle) {
      type = `${type}(${subtitle.toLowerCase()})`
    }
    return type
  }

  #formatSubtitleAsPath(subtitle) {
    // remove the method from the subtitle
    return subtitle.replace(/(GET|POST|PUT|PATCH|DELETE|OPTIONS|HEAD|TRACE|SUBSCRIBE|PUBLISH)/, "")
  }

  #rejectContentType(breadcrumbs) {
    return breadcrumbs.filter((breadcrumb) => {
      if (!breadcrumb) return false
      return !breadcrumb.startsWith("application/") &&
        !breadcrumb.startsWith("multipart/") &&
        !breadcrumb.startsWith("text/")
    })
  }

  #toggleMoreResults(results) {
    // if there are more results to load, show the more results button
    if ((results.estimatedTotalHits - (this.offsetValue + this.limitValue)) > 0) {
      this.#showShowMoreButton()
    } else {
      this.#hideShowMoreButton()
    }
  }

  #resetOffset() {
    this.offsetValue = 0
  }

  #incrementOffset() {
    this.offsetValue += this.limitValue
  }
}
